// Namespace „Kalender" — die Texte von
//   pages/CalendarMs.tsx        (Microsoft-365-Kalender unter /calendar)
//   pages/Calendar.tsx          (Faelligkeits-Kalender der Aufgaben)
//   pages/Abstimmungen.tsx      (Stand der Terminabstimmungen)
//   components/CalendarTimeGrid.tsx (Zeitraster Tag/Woche)
//
// NICHT hier drin und bewusst deutsch, auch in der englischen Oberflaeche:
//   * Datums- und Uhrzeit-FORMATE (`ui/germanDate.ts`, alle `de-DE`-Aufrufe,
//     `Intl.DateTimeFormat("de-DE", …)` in Abstimmungen.tsx) — inklusive der
//     Wochentags- und Monatsnamen, die daraus fallen.
//   * Die fest verdrahteten Wochentags-Kuerzel („Mo" … „So") der Monats- und
//     Aufgaben-Raster: sie stehen im selben Bild wie die aus `de-DE`
//     formatierten Kopfzeilen des Zeitrasters, und zwei Sprachen fuer dieselbe
//     Spalte waeren schlechter als eine deutsche.
//   * Das Muster `TT.MM.JJJJ` (die Eingabemaske selbst) und das Wort „Uhr"
//     hinter einer Uhrzeit — beides gehoert zum deutschen Format.
//   * `next_step` der Abstimmungen: dieser Satz kommt fertig vom Server
//     (app/schemas/calendar_poll.py) und ist Backend-Text (docs/i18n.md §5).
export const calendar = {
  seitenTitel: "Kalender",
  heute: "Heute",
  keineTermine: "Keine Termine",

  // ── Kalender-Seite: Werkzeugleiste + Leerzustaende ────────────────────────
  seite: {
    ansichtAria: "Ansicht",
    tag: "Tag",
    woche: "Woche",
    monat: "Monat",
    agenda: "Agenda",
    vor: "Vor",
    neuerTermin: "Neuer Termin",
    terminFinden: "Termin finden",
    abstimmungen: "Abstimmungen",
    // E2: Beschriftung des ···-Ueberlaufs. Bewusst dieselbe Wortwahl wie im
    // Posteingang (`mail.seite.weitereBefehle`) — derselbe Knopf, dieselbe
    // Bedeutung. Nicht in `common` zusammengezogen: die Kataloge halten ihre
    // Bereichs-Texte selbst, und ein Umzug des Mail-Keys waere ein Eingriff in
    // einen fremden Namespace (docs/i18n.md §4.1).
    weitereBefehle: "Weitere Befehle",
    nichtErreichbarTitel: "Kalender nicht erreichbar",
    nichtErreichbarHinweis:
      "Deine Termine lassen sich gerade nicht laden. Versuch es in einem Moment erneut.",
    nichtFreigeschaltetTitel: "Kalender noch nicht freigeschaltet",
    nichtFreigeschaltetHinweis:
      "Sobald dein Postfach für Atlas aktiviert ist, erscheinen deine Termine hier.",
  },

  // ── Ein Termin: Zeile, Raster-Block, Detail-Fenster ───────────────────────
  termin: {
    ganztaegig: "Ganztägig",
    keinBetreff: "(kein Betreff)",
    abgesagt: "Abgesagt",
    tagOeffnen: "Tag öffnen",
    mehr: (n: number) => `+${n} mehr`,
    keineTermineNaechste14: "Keine Termine in den nächsten 14 Tagen",

    zusagen: "Zusagen",
    mitVorbehalt: "Mit Vorbehalt",
    absagen: "Absagen",

    erinnerung: "Erinnerung",
    erinnerungTitel: "Erinnerung vor Terminbeginn",
    erinnerungKeine: "Keine",
    erinnerungMinuten: (n: number) => `${n} Min.`,
    erinnerungEinTag: "1 Tag",

    keineWeiterenTeilnehmer: "Keine weiteren Teilnehmer",
    teilnehmerAnzahl: (n: number) => `Teilnehmer (${n})`,
    wenigerAnzeigen: "weniger anzeigen",
    weitereAnzeigen: (n: number) => `+${n} weitere anzeigen`,
    teamsBeitreten: "Teams-Meeting beitreten",
    antwortAnOrganisator: "Antwort an den Organisator",
    keineTerminId:
      "Dieser Termin trägt keine Termin-ID — Antworten, Bearbeiten und Löschen sind dafür nicht möglich.",

    // Graph-Antwortstatus als Wort (antwortText)
    antwortZugesagt: "zugesagt",
    antwortAbgesagt: "abgesagt",
    antwortMitVorbehalt: "mit Vorbehalt",
    antwortOrganisator: "Organisator",
    antwortOffen: "offen",

    // Bestaetigung nach einer eigenen Antwort (Toast)
    respondZugesagt: "Zugesagt",
    respondAbgesagt: "Abgesagt",
    respondMitVorbehalt: "Mit Vorbehalt zugesagt",
    antwortFehlgeschlagen: "Antwort fehlgeschlagen",
    dienstNichtErreichbarKurz: "Kalender-Dienst nicht erreichbar.",

    loeschenTitel: "Termin löschen?",
    loeschenFrage: (betreff: string) =>
      `„${betreff}\" wird gelöscht. Das lässt sich nicht rückgängig machen.`,
    geloescht: "Termin gelöscht",
  },

  // ── Termin bearbeiten ─────────────────────────────────────────────────────
  bearbeiten: {
    titel: "Termin bearbeiten",
    betreff: "Betreff",
    beginnDatum: "Beginn (Datum)",
    beginnUhrzeit: "Beginn (Uhrzeit)",
    ariaBeginnDatum: "Beginn Datum (TT.MM.JJJJ)",
    ariaBeginnUhrzeit: "Beginn Uhrzeit",
    endeDatum: "Ende (Datum)",
    endeUhrzeit: "Ende (Uhrzeit)",
    ariaEndeDatum: "Ende Datum (TT.MM.JJJJ)",
    ariaEndeUhrzeit: "Ende Uhrzeit",
    bitteTtMmJjjj: "Bitte als TT.MM.JJJJ",
    platzhalterDatum: "TT.MM.JJJJ",
    endeNachBeginn: "Das Ende muss nach dem Beginn liegen.",
    aktualisiert: "Termin aktualisiert",
    aenderungGespeichert: "Die Änderung wurde gespeichert.",
    keinZugriff: "Kein Zugriff auf diesen Kalender.",
    dienstNichtErreichbar:
      "Kalender-Dienst nicht erreichbar. Bitte erneut versuchen.",
    speichernFehlgeschlagen: "Speichern fehlgeschlagen.",
  },

  // ── Neuer Termin (Raster-Klick / Werkzeugleiste) ──────────────────────────
  neu: {
    titel: "Neuer Termin",
    // Kopfzeile des Dialogs, solange die Eingabe noch nicht traegt
    datumUnvollstaendig: "Datum unvollständig",
    zeitspanneUngueltig: "Zeitspanne ungültig",
    einladungSenden: "Einladung senden",
    terminAnlegen: "Termin anlegen",
    betreff: "Betreff",
    betreffPlatzhalter: "z. B. Abstimmung Registrierung",
    datum: "Datum",
    ariaDatum: "Datum (TT.MM.JJJJ)",
    beginn: "Beginn",
    ariaStartzeit: "Startzeit",
    dauer: "Dauer",
    ariaDauer: "Dauer",
    enddatum: "Enddatum",
    enddatumHinweis: "Nur für mehrtägige Termine — sonst leer lassen.",
    ariaEnddatum: "Enddatum (TT.MM.JJJJ, nur für mehrtägige Termine)",
    // „Beginn 18:00 + 8 Std. reicht über Mitternacht hinaus. … die TÄGLICHE
    // Zeitspanne — …" (das Wort dazwischen steht fett, deshalb drei Teile).
    mitternachtBeginn: "Beginn",
    mitternachtRest1:
      "reicht über Mitternacht hinaus. Bei einem mehrtägigen Termin ist das die",
    mitternachtTaeglich: "tägliche",
    mitternachtRest2:
      "Zeitspanne — verkleinere die Dauer oder lass das Enddatum leer.",
    teilnehmerEinladen: "Teilnehmer einladen (nur für mich)",
    teilnehmerEingeladen: (n: number) => `${n} Teilnehmer eingeladen`,
    extern: (n: number) => `${n} extern`,
    zuklappen: "Zuklappen",
    auswaehlen: "Auswählen",
    onlineMeeting: "Online-Meeting (Teams)",
    beschreibung: "Beschreibung",
    beschreibungPlatzhalter: "Kurzer Kontext …",
    endeNachBeginn: "Das Ende muss nach dem Beginn liegen.",
    fehlerNichtEinladbar:
      "Mindestens eine Teilnehmer-Adresse ist nicht einladbar (kein aktiver bitop-Kollege und kein Salesforce-Kontakt).",
    fehlerDienst: "Kalender-Dienst nicht erreichbar. Bitte erneut versuchen.",
    fehlerAnlegen: "Termin konnte nicht angelegt werden.",
    einladungGesendet: "Einladung gesendet",
    angelegt: "Termin angelegt",
    teamsLinkImTermin: "Teams-Link liegt im Termin.",
  },

  // ── Teilnehmer-Auswahl (Picker, Chips, Pflicht/Optional-Gruppen) ──────────
  picker: {
    suchePlatzhalter: "Name oder E-Mail tippen …",
    sucheAria: "Teilnehmer suchen (Kollegen und externe Kontakte)",
    badgeExtern: "Extern",
    badgeFunktionspostfach: "Funktionspostfach",
    weitererAccount: (n: number) => `+${n} weiterer Account`,
    kollegenlisteAusblenden: "Kollegenliste ausblenden",
    alleKollegenAnzeigen: "Alle Kollegen anzeigen",
    selbstNichtInListe:
      "Du selbst stehst nicht in der Liste — als Organisator bist du automatisch dabei.",
    selbstNichtEinladbar:
      "Dich selbst kannst du nicht einladen — als Organisator bist du automatisch dabei.",
    keineWeiterenKollegen: "Keine weiteren Kollegen.",
    mindestensZeichen: (n: number) =>
      `Mindestens ${n} Zeichen — Kollegen und externe Salesforce-Kontakte.`,
    wirdGesucht: "Wird gesucht …",
    nichtInSalesforce:
      "ist in Salesforce nicht als Kontakt hinterlegt. Einladbar sind Kollegen und Salesforce-Kontakte — ohne Kontakt-Datensatz weist der Server die Adresse ab.",
    keineTreffer: "Keine Treffer.",
    trefferVonMax: (max: number) => `von max. ${max} Treffern`,
    externeNichtDurchsuchbar:
      "Externe Kontakte gerade nicht durchsuchbar (Salesforce) — Kollegen findest du weiterhin.",
    // Tastatur-Hinweis. Die Tastennamen stehen fett und deshalb einzeln.
    tasteEnter: "Enter",
    tasteTab: "Tab",
    tasteRuecktaste: "Rücktaste",
    hinweisAuswaehlen: "auswählen ·",
    hinweisUebernimmt: "übernimmt",
    hinweisTabSpringt: "springt durch die Liste ·",
    hinweisEntferntLetzten: "entfernt den letzten",
    hinweisEntferntLetztenTeilnehmer: "entfernt den letzten Teilnehmer",

    chipExtern: "· Extern",
    chipVerschiebenAria: (name: string, ziel: string) =>
      `${name} zu ${ziel} verschieben`,
    chipZu: (ziel: string) => `Zu ${ziel}`,
    chipEntfernenAria: (name: string) => `${name} entfernen`,

    rollePflicht: "Pflicht",
    rolleOptional: "Optional",
    rollePflichtHinweis: "Ein Slot gilt nur, wenn alle hier können.",
    rolleOptionalHinweis:
      "Wird eingeladen, blockiert aber keinen Slot — die Slot-Liste sagt nur, wer nicht kann.",
    rollePflichtLeer: "Niemand — nur dein Kalender zählt.",
    rolleOptionalLeer: "Niemand optional.",

    externWarnungEiner:
      "1 externer Teilnehmer — die Einladung verlässt das Haus",
    externWarnungMehrere: (n: number) =>
      `${n} externe Teilnehmer — die Einladung verlässt das Haus`,

    pruefenKopf: "Teilnehmer werden geprüft …",
    pruefenWird: "wird",
    pruefenWerden: "werden",
    pruefenRest: (sekunden: number) =>
      `gerade in Salesforce nachgeschlagen. Senden ist so lange gesperrt; je Adresse wird höchstens ${sekunden} Sekunden gewartet.`,
    unbekanntIst: "ist",
    unbekanntSind: "sind",
    unbekanntRest:
      "in Salesforce nicht als Kontakt hinterlegt. Ohne Kontakt-Datensatz weist der Server die Einladung ab — Teilnehmer entfernen oder den Kontakt in Salesforce anlegen.",
    ungeprueftKopf:
      "Der Kontakt-Abgleich mit Salesforce ist nicht durchgekommen — ob",
    ungeprueftRest:
      "als Kontakt hinterlegt ist, konnte nicht geprüft werden. Senden bleibt möglich; der Server entscheidet.",
  },

  // ── „Termin finden"-Wizard ────────────────────────────────────────────────
  wizard: {
    titel: "Termin finden",
    beschreibung: "Gemeinsame freie Zeit finden und einladen.",
    schrittTeilnehmerZeitraum: "Teilnehmer & Zeitraum",
    schrittSlot: "Slot",
    schrittEinladung: "Einladung",
    schrittAbstimmung: "Abstimmung",

    zeitraum: "Zeitraum",
    dieseWoche: "Diese Woche",
    naechsteWoche: "Nächste Woche",
    naechste14Tage: "Nächste 14 Tage",
    dauer: "Dauer",
    minuten: (n: number) => `${n} min`,

    // 20-Kalender-Grenze (Pflicht)
    capOutlookPrueft: "Outlook prüft höchstens",
    capKalender: (n: number) => `${n} Kalender`,
    capPflichtMitte: (intern: number, gesamt: number) =>
      `auf einmal. Du und ${intern} interne Pflichtteilnehmer sind ${gesamt} —`,
    capZuViel: (n: number) => `${n} zu viel`,
    capEntferne: (n: number) => `. Entferne ${n} Teilnehmer oder verschiebe`,
    capIhn: "ihn",
    capSie: "sie",
    capPflichtRest:
      "auf Optional — Optionale werden bei der Suche weggelassen, bekommen die Einladung aber.",
    // 20-Kalender-Grenze (Optionale fallen aus der Suche)
    capOptionalMitte: (belegt: number) =>
      `auf einmal — du und die Pflichtteilnehmer belegen davon schon ${belegt}.`,
    capOptionalEiner: "Der optionale Teilnehmer wird",
    capOptionalMehrere: (n: number) => `Die ${n} optionalen Teilnehmer werden`,
    capOptionalDeshalb: "deshalb",
    capOptionalNichtGeprueft: "nicht auf Verfügbarkeit geprüft",
    capOptionalEinladung: "— die Einladung",
    capOptionalBekommtEr: "bekommt er",
    capOptionalBekommenSie: "bekommen sie",
    capOptionalTrotzdem: "trotzdem.",

    fuer: "Für",
    einOptionalerTeilnehmer: "1 optionalen Teilnehmer",
    optionaleTeilnehmer: (n: number) => `${n} optionale Teilnehmer`,
    einExternerTeilnehmer: "1 externen Teilnehmer",
    externeTeilnehmer: (n: number) => `${n} externe Teilnehmer`,
    ist: "ist",
    keineVerfuegbarkeitBekannt: "keine Verfügbarkeit bekannt",
    optionalUnbekanntRest: (namen: string) =>
      `— Outlook gibt für ${namen} keinen Kalender her. Die Vorschläge sagen darüber nichts; die Einladung`,
    bekommtErOderSie: "bekommt er oder sie",
    bekommenSie: "bekommen sie",
    trotzdem: "trotzdem.",
    externAvailRest:
      "— Outlook sieht keine Kalender außerhalb des Hauses. Die Vorschläge gelten nur für dich und die internen Teilnehmer;",
    externAvailBekommt: "bekommt die Einladung trotzdem.",

    nurDeinKalender: "Nur dein Kalender wird geprüft.",
    pflichtteilnehmerGeprueft: (n: number) =>
      `Prüfe gemeinsame freie Zeit mit ${n} internen Pflichtteilnehmer${n > 1 ? "n" : ""}.`,
    optionaleMitgelesen: (n: number) =>
      ` ${n} optionale Kalender werden mitgelesen — sie blockieren keinen Slot.`,
    vorschlaegeZwischen: (von: string, bis: string) =>
      `Vorschläge zwischen ${von} und ${bis} — änderbar in den Einstellungen unter „Kalender\".`,
    geschaeftszeitenNichtGeladen:
      "Deine Geschäftszeiten liessen sich gerade nicht laden — die Vorschläge folgen trotzdem der Einstellung deines Kontos.",
    zuVielePflichtteilnehmer: (max: number) =>
      `Zu viele Pflichtteilnehmer für die Verfügbarkeits-Prüfung (max. ${max} Kalender)`,
    freieSlotsSuchen: "Freie Slots suchen",

    // Selbst eingetragene Kundenzeiten
    kundenzeitenKopf: "Zeiten vom Kunden eintragen.",
    kundenzeitenRest:
      "Hat der Kunde selbst Termine genannt, kommen sie hier herein — auch außerhalb deiner Geschäftszeiten. Neben jeder Zeit steht dann, ob die internen Teilnehmer dann können.",
    datum: "Datum",
    bitteTtMmJjjj: "Bitte als TT.MM.JJJJ",
    platzhalterDatum: "TT.MM.JJJJ",
    ariaKundenzeitDatum: "Datum der Kundenzeit (TT.MM.JJJJ)",
    beginn: "Beginn",
    ariaKundenzeitUhrzeit: "Uhrzeit der Kundenzeit",
    zeitUebernehmen: "Zeit übernehmen",
    dauerWieSchrittEins: (dauer: string) =>
      `Dauer ${dauer} — wie in Schritt 1 eingestellt. Alle zur Wahl gestellten Termine sind gleich lang.`,
    bitteDatumEintragen: "Bitte ein Datum als TT.MM.JJJJ eintragen.",
    zeitSchonInListe: "Diese Zeit steht schon in der Liste.",
    mehrAlsNZeiten: (max: number) => `Mehr als ${max} Zeiten gehen nicht.`,
    mehrAlsNZeitenEntfernen: (max: number) =>
      `Mehr als ${max} Zeiten gehen nicht — zuerst eine entfernen.`,
    terminInVergangenheit:
      "Der Termin liegt in der Vergangenheit — darüber lässt sich nicht mehr abstimmen.",
    eingetrageneZeiten: (n: number, dauer: number) =>
      `${n} eingetragene ${n === 1 ? "Zeit" : "Zeiten"} · ${dauer} min`,

    // Befund einer eingetragenen Zeit
    verfuegbarkeitWirdGeprueft: "Verfügbarkeit wird geprüft …",
    verfuegbarkeitUnbekannt: "Verfügbarkeit unbekannt",
    istBelegt: (namen: string, anzahl: number) =>
      `${namen} ${anzahl === 1 ? "ist" : "sind"} belegt`,
    internFreiAber: (namen: string, anzahl: number) =>
      `Intern frei — ${namen} (optional) ${anzahl === 1 ? "kann" : "können"} nicht`,
    freiSoweitKalender: "Frei, soweit Kalender vorliegen",
    internFrei: "Intern frei",
    du: "Du",

    // Markierung an einem gefundenen Vorschlag
    optionalUnbekannt: "Optional: unbekannt",
    optionalKannNicht: (namen: string, anzahl: number) =>
      `${namen} ${anzahl === 1 ? "kann" : "können"} nicht`,
    alleMitBekanntemKalender: "Alle mit bekanntem Kalender können",
    alleKoennen: "Alle können",

    slotGesperrt: (max: number) =>
      `Höchstens ${max} Vorschläge — zuerst einen abwählen`,
    zeitEntfernenAria: (datum: string, zeit: string) =>
      `Zeit ${datum} ${zeit} entfernen`,

    slotsWerdenGesucht: "Freie Zeitfenster werden gesucht …",
    fehlerNichtEinladbar:
      "Mindestens eine Teilnehmer-Adresse ist nicht einladbar (kein aktiver bitop-Kollege und kein Salesforce-Kontakt).",
    fehlerNichtPruefbar:
      "Mindestens eine Teilnehmer-Adresse ist nicht prüfbar (die Slot-Suche kennt nur interne Kalender).",
    fehlerDienstSuche: "Der Kalender-Dienst ist gerade nicht erreichbar.",
    fehlerFormatSuche:
      "Die Anfrage an den Kalender-Dienst war fehlerhaft (technischer Fehler). Bitte melde das — an den Teilnehmern liegt es nicht.",
    fehlerSuche: "Die Slot-Suche ist fehlgeschlagen.",
    teilnehmerPruefen: "Teilnehmer prüfen",
    keineGemeinsamenSlots: "Keine gemeinsamen freien Slots im gewählten Zeitraum.",
    kundenzeitTrotzdem:
      "Eine vom Kunden genannte Zeit kannst du oben trotzdem eintragen — auch außerhalb deiner Geschäftszeiten.",
    zeitraumErweitern: "Zeitraum erweitern",
    slotZaehlerGekappt: (gezeigt: number, gesamt: number, dauer: number) =>
      `${gezeigt} von ${gesamt} freien Zeitfenstern · ${dauer} min`,
    slotZaehler: (gesamt: number, dauer: number) =>
      `${gesamt} freie Zeitfenster · ${dauer} min`,
    optionaleNichtsGemeldetKopf:
      "Zu den optionalen Teilnehmern hat der Kalender-Dienst nichts mitgeliefert — ob sie können, ist",
    offen: "offen",
    optionaleNichtsGemeldetRest:
      ". Die Vorschläge gelten für dich und die Pflichtteilnehmer.",

    mehrereTermineWaehlbar: "Mehrere Termine wählbar",
    hoechstensN: (max: number) =>
      `(höchstens ${max}) — die Teilnehmer bekommen sie zur Wahl gestellt.`,
    vonN: (gewaehlt: number, max: number) => `${gewaehlt} von ${max}`,
    gewaehlt: "gewählt —",
    nochKeinerAusgewaehlt: "noch ist keiner ausgewählt.",
    nTermineZurAbstimmung: (n: number) =>
      `die ${n} Termine gehen zur Abstimmung, gebucht wird noch nichts.`,
    einzelnerTerminGebucht: "ein einzelner Termin wird direkt gebucht.",
    capVorschlaegeKopf: (max: number) =>
      `Mehr als ${max} Vorschläge gibt es nicht — jede weitere Option kostet den Empfänger einen Kalender-Abgleich und eine Antwort seltener. Für einen anderen Termin zuerst einen`,
    abwaehlen: "abwählen",
    nochNVorschlaege: (n: number) => `Noch ${n} Vorschläge anzeigen`,
    weiter: "Weiter",

    betreff: "Betreff",
    betreffPlatzhalter: "z. B. Abstimmung Registrierung",
    beschreibungFeld: "Beschreibung",
    beschreibungPlatzhalter: "Kurzer Kontext für die Einladung …",
    zusammenfassung: "Zusammenfassung",
    duOrganisator: "Du (Organisator)",
    keineWeiterenTeilnehmer: " — keine weiteren Teilnehmer",
    optionalListe: (namen: string) => `Optional: ${namen}`,
    externSuffix: (firma: string | null) =>
      firma ? ` (extern, ${firma})` : " (extern)",
    nochNichtsGebucht: "Es wird noch nichts gebucht.",
    nochNichtsGebuchtRest: (n: number) =>
      `Diese ${n} Termine gehen als Auswahl an die Teilnehmer; gebucht wird erst, wenn sie geantwortet haben. Bis dahin bleiben alle ${n} Zeiten in deinem Kalender frei.`,
    brauchtPflichtKopf: "Für eine Abstimmung braucht es mindestens einen",
    pflichtTeilnehmer: "Pflicht-Teilnehmer",
    brauchtPflichtRest:
      "— sonst steht kein Kalender hinter der Zusage. Verschiebe dafür in Schritt 1 jemanden von Optional auf Pflicht.",
    internRundeKopf:
      "In dieser Runde stehen nur interne Teilnehmer. Sobald alle Pflicht-Teilnehmer zugesagt haben, wird",
    internRundeFett: "genau mit diesen Personen gebucht",
    internRundeRest:
      "— ein Kunde, der die Zeiten vorgeschlagen hat, bekommt keine Einladung. Trag ihn in Schritt 1 als Teilnehmer ein, wenn er dabei sein soll.",
    betreffFehltNoch: "Betreff fehlt noch",
    fehlerExtern:
      "Mindestens eine Teilnehmer-Adresse ist extern — dafür gibt es hier keine Verfügbarkeit.",
    fehlerDienstSenden: "Kalender-Dienst nicht erreichbar. Bitte erneut versuchen.",
    fehlerFormatSenden: (mitAbstimmung: boolean) =>
      `Die Anfrage war fehlerhaft (technischer Fehler). Es wurde nichts verschickt${
        mitAbstimmung ? " und keine Abstimmung angelegt" : ""
      } — an den Teilnehmern liegt es nicht. Bitte melde das.`,
    fehlerAbstimmung:
      "Die Abstimmung konnte nicht angelegt werden. Es wurde nichts verschickt.",
    fehlerEinladung: "Einladung konnte nicht gesendet werden.",
    rueckfrageAbstimmung: (n: number) =>
      `${n} Termine jetzt zur Abstimmung stellen?`,
    rueckfrageSenden: "Einladung jetzt senden?",
    jaZurAbstimmung: "Ja, zur Abstimmung",
    jaSenden: "Ja, senden",
    zurAbstimmungStellen: "Zur Abstimmung stellen",
    einladungSenden: "Einladung senden",
    titelNichtEinladbar: "Nicht einladbare Adresse in der Teilnehmerliste",
    titelWirdGeprueft: "Teilnehmer werden noch in Salesforce geprüft",
    titelBrauchtPflicht:
      "Eine Abstimmung braucht mindestens einen Pflicht-Teilnehmer",
    toastEinladungGesendet: "Einladung gesendet",
    toastTerminErstellt: "Der Termin wurde erstellt.",
    toastAbstimmungAngelegt: "Abstimmung angelegt",
    toastTermineZurWahl: "Die Termine stehen zur Wahl.",

    // Abschluss: Abstimmung angelegt
    zurAbstimmungGestellt: "Zur Abstimmung gestellt",
    termineStehenZurWahl: (n: number) => `${n} Termine stehen zur Wahl —`,
    gebuchtIstNochNichts: "gebucht ist noch nichts.",
    laeuftBis: "Die Abstimmung läuft bis",
    laeuftBisRest: (zeit: string) =>
      `, ${zeit} Uhr — also bis zum frühesten Vorschlag; danach wäre sie gegenstandslos. Sobald die Teilnehmer geantwortet haben, wird der Termin gebucht.`,
    einladungEinerBekommen:
      "Der Teilnehmer hat seinen persönlichen Abstimmungs-Link per Mail bekommen.",
    einladungAlleBekommen: (n: number) =>
      `Alle ${n} Teilnehmer haben ihren persönlichen Abstimmungs-Link per Mail bekommen.`,
    einladungenFehlgeschlagen: (fehl: number, gesamt: number) =>
      `${fehl} von ${gesamt} Einladungen ging nicht raus`,
    einladungenAbstimmungSteht: (adressen: string) =>
      `(${adressen}). Die Abstimmung steht —`,
    dieserEmpfaengerWeiss: "dieser Empfänger weiß",
    dieseEmpfaengerWissen: "diese Empfänger wissen",
    nochNichtsDavon: "aber noch nichts davon.",
    keineMailKopf:
      "Über eingehende Antworten schickt Atlas keine Mail — den Stand siehst du unter",
    abstimmungenLink: "Abstimmungen",
    keineMailRest: ", jederzeit auch später über die Kalender-Leiste.",

    // Abschluss: Termin gebucht
    terminErstellt: "Termin erstellt",
    einladungVersendet: "Die Einladung wurde an alle Teilnehmer versendet.",
    teamsMeetingOeffnen: "Teams-Meeting öffnen",
    inOutlookOeffnen: "In Outlook öffnen",
  },

  // ── Terminabstimmungen (pages/Abstimmungen.tsx) ───────────────────────────
  polls: {
    dokumentTitel: "Terminabstimmungen – Atlas",
    seitenTitel: "Terminabstimmungen",
    seitenBeschreibung:
      "Wer hat schon geantwortet, und was passiert als Nächstes.",

    zustandGebucht: "Gebucht",
    zustandWirdGebucht: "Wird gebucht",
    zustandBuchungFehlgeschlagen: "Buchung fehlgeschlagen",
    zustandKeinGemeinsamerTermin: "Kein gemeinsamer Termin",
    zustandAbgesagt: "Abgesagt",
    zustandFristAbgelaufen: "Frist abgelaufen",
    zustandLaeuft: "Läuft",

    stoerungTitel: "Abstimmungen nicht erreichbar",
    stoerungHinweis:
      "Der Stand deiner Terminabstimmungen lässt sich gerade nicht laden. Versuch es in einem Moment erneut.",
    leerTitel: "Noch keine Terminabstimmung",
    leerHinweis:
      "Sobald du im Kalender mehrere Termine zur Wahl stellst, steht der Stand hier.",
    zumKalender: "Zum Kalender",

    laeuftNoch: "Läuft noch",
    laeuftNochLeer: "Gerade läuft keine Abstimmung.",
    abgeschlossen: "Abgeschlossen",
    habenAbgestimmt: (geantwortet: number, gesamt: number) =>
      `${geantwortet} von ${gesamt} haben abgestimmt`,
    vorschlaege: (n: number) => `${n} Vorschläge`,
    alleHabenAbgestimmt: "Alle haben abgestimmt.",
    eineRueckmeldungFehlt: "Es fehlt noch 1 Rückmeldung.",
    rueckmeldungenFehlen: (n: number) => `Es fehlen noch ${n} Rückmeldungen.`,
    frist: "Frist:",
    fristZeit: (zeit: string) => `, ${zeit} Uhr`,
    /** Steht hinter einer bereits formatierten Uhrzeit. In BEIDEN Sprachen
     *  deutsch — dieselbe Klasse wie die Wochentagsnamen aus `de-DE`. */
    uhrSuffix: " Uhr",
    dauerUndFrist: (minuten: number) => `${minuten} Minuten · Frist`,

    alleAbstimmungen: "Alle Abstimmungen",
    gibtEsNichtTitel: "Diese Abstimmung gibt es nicht",
    gibtEsNichtHinweis:
      "Sie wurde gelöscht, oder sie gehört zu einem anderen Postfach.",
    buchungsfehler: (grund: string) => `Fehler bei der Buchung: ${grund}`,

    kennzahlEingeladen: "Eingeladen",
    kennzahlAbgestimmt: "Abgestimmt",
    kennzahlOffen: "Offen",
    zustimmungJeVorschlag: "Zustimmung je Vorschlag",
    nochKeineZusage: "Noch keine Zusage",
    niemandAbgestimmt: "Bisher hat niemand über den Link abgestimmt.",
    niemandAbgestimmtOffen:
      "Das ist normal, solange die Frist läuft — geantwortet wird oft erst kurz davor.",
    niemandAbgestimmtBeendet:
      "Wer per E-Mail geantwortet hat, taucht hier nicht auf: gezählt wird nur, was über den persönlichen Link kommt.",

    eingeladeneAnzahl: (n: number) => `Eingeladene (${n})`,
    optionalChip: "optional",
    einladungNichtZugestellt: "Einladung konnte nicht zugestellt werden",
    erinnertAm: (zeitpunkt: string) => `Erinnert am ${zeitpunkt} Uhr`,
    kannKeinen: "Kann keinen",
    eineZeit: "1 Zeit",
    nZeiten: (n: number) => `${n} Zeiten`,
    keineAntwort: "Keine Antwort",
    keineAntwortErklaerung:
      "„Keine Antwort\" heißt: über den persönlichen Link kam nichts. Eine Antwort per gewöhnlicher E-Mail sieht Atlas nicht — sie steht in deinem Postfach.",
    gebuchtenTerminOeffnen: "Gebuchten Termin in Outlook öffnen",
  },

  // ── Faelligkeits-Kalender der Aufgaben (pages/Calendar.tsx) ───────────────
  aufgaben: {
    titel: "Kalender",
    perDragVerschieben: "Aufgaben per Drag verschieben",
    neuLaden: "reload",
    zurueck14Tage: "zurueck 14 Tage",
    vor14Tage: "vor 14 Tage",
    heuteKlein: "heute",
    heute: "Heute",
    nurMeine: "Nur meine",
    erledigte: "Erledigte",
    salesforce: "Salesforce",
    vorherigerMonat: "previous month",
    naechsterMonat: "next month",
    anzahlAufgaben: (n: number) => `${n} Aufgaben`,
    weitere: (n: number) => `+${n} weitere`,
    keineTermine: "keine Termine",
    ladenFehlgeschlagen: "Laden fehlgeschlagen",
    sfNichtVerschiebbar:
      "Salesforce-Aufgaben können nicht verschoben werden — in Salesforce bearbeiten.",
    verschiebenFehlgeschlagen: (grund: string) =>
      `Verschieben fehlgeschlagen: ${grund}`,
  },
};

export type CalendarKatalog = typeof calendar;
